import { useState } from 'react';
import { Phone, Mail, MapPin, Clock, CheckCircle } from 'lucide-react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    systemType: '',
    message: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submission (demo only):', formData);
    setIsSubmitted(true);
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({
        name: '',
        phone: '',
        email: '',
        address: '',
        systemType: '',
        message: ''
      });
    }, 5000);
  };

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="bg-[var(--color-slate-blue)] text-white py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white">Contact Us</h1>
          <p className="text-xl text-gray-100 max-w-3xl">
            Tell us what's going on with your system. We'll respond within two business hours during the week, 
            same day on weekends for emergencies.
          </p>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8 mb-12">
            <div className="bg-[var(--color-neutral-bg)] rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-[var(--color-slate-blue)] text-white p-3 rounded-lg">
                  <Phone size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-[var(--color-text-dark)]">Call Us</h3>
                  <a href="tel:9055550142" className="text-[var(--color-slate-blue)] font-semibold text-lg">
                    (905) 555-0142
                  </a>
                </div>
              </div>
              <p className="text-gray-600">
                Available 7 days a week for emergencies. Regular service hours Mon-Fri 7:30 AM - 6:00 PM, 
                Sat 8:00 AM - 4:00 PM.
              </p>
            </div>

            <div className="bg-[var(--color-neutral-bg)] rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-[var(--color-slate-blue)] text-white p-3 rounded-lg">
                  <Mail size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-[var(--color-text-dark)]">Email</h3>
                  <span className="text-[var(--color-slate-blue)] font-semibold">
                    service@northbridgehvac.ca
                  </span>
                </div>
              </div>
              <p className="text-gray-600">
                For non-urgent inquiries and documentation. We respond to emails within one business day.
              </p>
            </div>

            <div className="bg-[var(--color-neutral-bg)] rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-[var(--color-slate-blue)] text-white p-3 rounded-lg">
                  <MapPin size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-[var(--color-text-dark)]">Service Area</h3>
                  <span className="text-[var(--color-slate-blue)] font-semibold">
                    York Region & North Toronto
                  </span>
                </div>
              </div>
              <p className="text-gray-600">
                Serving Vaughan, Richmond Hill, Markham, Aurora, Newmarket, and surrounding areas.
              </p>
            </div>
          </div>

          {/* Business Hours */}
          <div className="bg-white border border-gray-200 rounded-lg p-8 mb-12 max-w-2xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <Clock size={28} className="text-[var(--color-slate-blue)]" />
              <h2 className="text-2xl font-bold text-[var(--color-text-dark)]">Business Hours</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <h3 className="font-semibold text-[var(--color-text-dark)] mb-3">Regular Service</h3>
                <div className="space-y-2 text-gray-600">
                  <div className="flex justify-between">
                    <span>Monday - Friday:</span>
                    <span>7:30 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Saturday:</span>
                    <span>8:00 AM - 4:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Sunday:</span>
                    <span>Closed</span>
                  </div>
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-[var(--color-text-dark)] mb-3">Emergency Service</h3>
                <div className="space-y-2 text-gray-600">
                  <p>Available 7 days a week for:</p>
                  <ul className="ml-4 space-y-1">
                    <li>• No heat emergencies</li>
                    <li>• No cooling emergencies</li>
                    <li>• Gas leaks</li>
                    <li>• Carbon monoxide concerns</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center text-[var(--color-text-dark)]">
              Book a Service Call
            </h2>
            
            {isSubmitted ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
                <CheckCircle size={48} className="text-green-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-green-800 mb-2">Thank You!</h3>
                <p className="text-green-700">
                  Your service request has been received (demo mode). In a real scenario, 
                  we would contact you within 2 business hours to schedule your service call.
                </p>
                <p className="text-sm text-gray-600 mt-4 italic">
                  Note: This is a portfolio demonstration site. No actual service request has been submitted.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--color-slate-blue)] focus:border-transparent"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Phone <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--color-slate-blue)] focus:border-transparent"
                      placeholder="(905) 555-1234"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--color-slate-blue)] focus:border-transparent"
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="systemType" className="block text-sm font-medium text-gray-700 mb-2">
                      System Type
                    </label>
                    <select
                      id="systemType"
                      name="systemType"
                      value={formData.systemType}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--color-slate-blue)] focus:border-transparent"
                    >
                      <option value="">Select your system</option>
                      <option value="furnace">Furnace</option>
                      <option value="ac">Air Conditioner</option>
                      <option value="heat-pump">Heat Pump</option>
                      <option value="ductless">Ductless Mini-Split</option>
                      <option value="hrv">HRV/ERV</option>
                      <option value="other">Other/Not Sure</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                    Service Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    required
                    value={formData.address}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--color-slate-blue)] focus:border-transparent"
                    placeholder="123 Main St, Vaughan, ON L4L 1A1"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Describe the Issue <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={4}
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--color-slate-blue)] focus:border-transparent"
                    placeholder="Please describe what's happening with your system..."
                  />
                </div>

                <div className="bg-[var(--color-neutral-bg)] rounded-lg p-4">
                  <p className="text-sm text-gray-600 italic">
                    <strong>Note:</strong> This is a demonstration form on a portfolio site. 
                    No actual service request will be submitted.
                  </p>
                </div>

                <div className="text-center">
                  <button
                    type="submit"
                    className="bg-[var(--color-warm-amber)] text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-opacity-90 transition-colors"
                  >
                    Submit Service Request
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Emergency CTA */}
      <section className="py-12 bg-red-50 border-t-4 border-red-500">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold mb-4 text-red-800">
            Emergency? No Heat or No Cooling?
          </h2>
          <p className="text-lg text-red-700 mb-6">
            Don't wait for email — call us directly for same-day emergency service.
          </p>
          <a 
            href="tel:9055550142" 
            className="inline-flex items-center gap-2 bg-red-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-red-700 transition-colors"
          >
            <Phone size={24} />
            Call Now: (905) 555-0142
          </a>
        </div>
      </section>
    </div>
  );
}